const chapter = {
  id: 'transport-in-plants',
  slug: 'transport-in-plants',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Transport in Plants',
        hindi: 'पादपों में परिवहन',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Plant Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'means-transport',
      title: {
        english: 'Means of Transport',
        hindi: 'परिवहन के साधन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Diffusion** — passive; high → low concentration; no ATP; slow over distance; gases, water, solutes.',
        hindi: '**प्रसार** - निष्क्रिय; उच्च → निम्न सांद्रता; कोई एटीपी नहीं; दूरी पर धीमी गति से; गैसें, पानी, विलेय।',
            },
            {
              english: '**Facilitated diffusion** — passive; via membrane proteins (porins, aquaporins); high → low; specific.',
        hindi: '**सुविधाजनक प्रसार** - निष्क्रिय; झिल्ली प्रोटीन (पोरिन, एक्वापोरिन) के माध्यम से; उच्च → निम्न; विशिष्ट।',
            },
            {
              english: '**Active transport** — uses **ATP**; against gradient; via pumps (e.g. Na⁺/K⁺); saturable; sensitive to inhibitors.',
        hindi: '**सक्रिय परिवहन** — **एटीपी** का उपयोग करता है; ढाल के विरुद्ध; पंपों के माध्यम से (जैसे Na⁺/K⁺); संतृप्त; अवरोधकों के प्रति संवेदनशील।',
            },
            {
              english: 'Comparison highlights:',
        hindi: 'तुलना पर प्रकाश डाला गया:',
            },
            {
              english: '• Active = uphill + ATP + carrier; Facilitated = downhill + carrier + no ATP; Simple diffusion = downhill + no carrier + no ATP.',
        hindi: '• सक्रिय = ऊपर की ओर + एटीपी + वाहक; सुविधायुक्त = ढलान + वाहक + कोई एटीपी नहीं; सरल प्रसार = डाउनहिल + कोई वाहक नहीं + कोई एटीपी नहीं।',
            },
          ],
        },
      ],
    },
    {
      id: 'water-potential',
      title: {
        english: 'Plant–Water Relations',
        hindi: 'पादप-जल सम्बन्ध',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Water potential ($\\Psi_w$)** = $\\Psi_s + \\Psi_p$ (in absence of solutes & gravity).',
        hindi: '**जल क्षमता ($\\Psi_w$)** = $\\Psi_s + \\Psi_p$ (विलेय पदार्थ और गुरुत्वाकर्षण की अनुपस्थिति में)।',
            },
            {
              english: '• $\\Psi_s$ = **solute potential** (always negative).',
        hindi: '• $\\Psi_s$ = **घुलनशील क्षमता** (हमेशा नकारात्मक)।',
            },
            {
              english: '• $\\Psi_p$ = **pressure potential** (positive in turgid cells; negative as tension in xylem).',
        hindi: '• $\\Psi_p$ = **दबाव क्षमता** (संदिग्ध कोशिकाओं में सकारात्मक; जाइलम में तनाव के रूप में नकारात्मक)।',
            },
            {
              english: 'Pure water at standard T,P → $\\Psi_w = 0$. Adding solute → $\\Psi_w$ becomes negative.',
        hindi: 'मानक T,P → $\\Psi_w = 0$ पर शुद्ध पानी। विलेय जोड़ने पर → $\\Psi_w$ ऋणात्मक हो जाता है।',
            },
            {
              english: '**Osmosis** — net diffusion of water across semi-permeable membrane down its $\\Psi_w$ gradient.',
        hindi: '**ऑस्मोसिस** - इसके $\\Psi_w$ ग्रेडिएंट के नीचे अर्ध-पारगम्य झिल्ली में पानी का शुद्ध प्रसार।',
            },
            {
              english: 'Plant cell in solutions:',
        hindi: 'समाधान में पादप कोशिका:',
            },
            {
              english: '• **Hypotonic** ($\\Psi_w$ outside > inside) → water in → **turgid** (endosmosis).',
        hindi: '• **हाइपोटोनिक** ($\\Psi_w$ बाहर > अंदर) → अंदर पानी → **टर्गिड** (एंडोस्मोसिस)।',
            },
            {
              english: '• **Isotonic** → no net movement.',
        hindi: '• **आइसोटोनिक** → कोई नेट मूवमेंट नहीं।',
            },
            {
              english: '• **Hypertonic** → water out → **plasmolysis** (protoplast shrinks); reversed by hypotonic = **deplasmolysis**.',
        hindi: '• **हाइपरटोनिक** → पानी बाहर → **प्लाज्मोलिसिस** (प्रोटोप्लास्ट सिकुड़ता है); hypotonic = **deplasmolysis** द्वारा उलट दिया गया।',
            },
            {
              english: '**Imbibition** — colloid absorbs water (seeds swell, dry wood splits); requires affinity + $\\Psi_w$ gradient.',
        hindi: '**इम्बिबिशन** - कोलाइड पानी को अवशोषित करता है (बीज फूल जाते हैं, सूखी लकड़ी फट जाती है); एफ़िनिटी + $\\Psi_w$ ग्रेडिएंट की आवश्यकता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'long-distance',
      title: {
        english: 'Long-Distance Transport',
        hindi: 'लम्बी दूरी का परिवहन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Diffusion too slow for >1 m → plants use **mass/bulk flow** through xylem (up) + phloem (up & down).',
        hindi: '1 मीटर तक प्रसार बहुत धीमा है → पौधे जाइलम (ऊपर) + फ्लोएम (ऊपर और नीचे) के माध्यम से **mass/bulk प्रवाह** का उपयोग करते हैं।',
            },
            {
              english: 'Three pathways water enters root:',
        hindi: 'तीन मार्गों से पानी जड़ में प्रवेश करता है:',
            },
            {
              english: '• **Apoplast** — through cell walls + intercellular spaces; no membrane crossing; until **endodermis (Casparian strip — suberin)** blocks → forced through symplast.',
        hindi: '• **एपोप्लास्ट** - कोशिका दीवारों + अंतरकोशिकीय स्थानों के माध्यम से; कोई झिल्ली क्रॉसिंग नहीं; जब तक **एंडोडर्मिस (कैस्पेरियन स्ट्रिप - सुबेरिन)** ब्लॉक न हो जाए → सिम्प्लास्ट के माध्यम से मजबूर न हो जाए।',
            },
            {
              english: '• **Symplast** — cytoplasm → cytoplasm via **plasmodesmata**.',
        hindi: '• **सिम्प्लास्ट** — साइटोप्लाज्म → साइटोप्लाज्म **प्लाज्मोडेस्माटा** के माध्यम से।',
            },
            {
              english: '• **Transmembrane** — through cell membranes (in + out of vacuole).',
        hindi: '• **ट्रांसमेम्ब्रेन** - कोशिका झिल्ली के माध्यम से (रिधानिका के अंदर + बाहर)। **जड़ दबाव** - आयनों के सक्रिय अवशोषण के कारण जाइलम में सकारात्मक दबाव विकसित हुआ; **गुटेशन** के रूप में दिखाई देता है (हाइड्रैथोड्स पर सुबह की बूंदें - *कोलोकेसिया*); ऊंचे पेड़ों में मामूली.',
            },
            {
              english: '**Root pressure** — positive pressure developed in xylem due to active absorption of ions; visible as **guttation** (early morning droplets at hydathodes — *Colocasia*); minor in tall trees.',
        hindi: '**जड़ दबाव** - आयनों के सक्रिय अवशोषण के कारण जाइलम में सकारात्मक दबाव विकसित हुआ; **गुटेशन** के रूप में दिखाई देता है (हाइड्रैथोड्स पर सुबह की बूंदें - *कोलोकेसिया*); ऊंचे पेड़ों में मामूली.',
            },
            {
              english: '**Transpiration pull / Cohesion-Tension theory (Dixon)** — main mechanism in tall trees.',
        hindi: '**वाष्पोत्सर्जन खिंचाव / सामंजस्य-तनाव सिद्धांत (डिक्सन)** - ऊंचे पेड़ों में मुख्य तंत्र।',
            },
            {
              english: '• Transpiration from leaves → tension in xylem → continuous water column pulled up due to **cohesion** (water-water H-bonds) + **adhesion** (water-xylem walls); accounts for movement to >100 m.',
        hindi: '• पत्तियों से वाष्पोत्सर्जन → जाइलम में तनाव → निरंतर जल स्तंभ **सामंजस्य** (जल-जल एच-बंध) + **आसंजन** (जल-जाइलम दीवारें) के कारण ऊपर खींचा जाता है; >100 मीटर तक की गति के लिए जिम्मेदार।',
            },
          ],
        },
      ],
    },
    {
      id: 'transpiration',
      title: {
        english: 'Transpiration',
        hindi: 'वाष्पोत्सर्जन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Loss of water as vapour from aerial parts; mostly through **stomata (90%+)**.',
        hindi: 'हवाई भागों से वाष्प के रूप में पानी की हानि; अधिकतर **रंध्र (90%+)** के माध्यम से।',
            },
            {
              english: 'Functions: creates transpiration pull; cools leaf surface (evaporative cooling); supplies water for photosynthesis; maintains turgor.',
        hindi: 'कार्य: वाष्पोत्सर्जन खिंचाव पैदा करता है; पत्ती की सतह को ठंडा करता है (वाष्पीकरणीय शीतलन); प्रकाश संश्लेषण के लिए जल की आपूर्ति करता है; स्फीति बनाए रखता है. **रंध्र का खुलना** - रक्षक कोशिकाएं स्फीत हो जाती हैं → बाहरी पतली दीवार उभरी हुई होती है, भीतरी मोटी दीवार बाहर निकल जाती है → छिद्र खुल जाते हैं।',
            },
            {
              english: '**Stomatal opening** — guard cells become turgid → outer thin wall bulges, inner thick wall pulled out → pore opens.',
        hindi: '**रंध्र का खुलना** — रक्षक कोशिकाएं स्फीत हो जाती हैं → बाहरी पतली दीवार उभरी हुई होती है, भीतरी मोटी दीवार बाहर निकल जाती है → छिद्र खुल जाते हैं।',
            },
            {
              english: 'Mechanism: **K⁺ uptake** by guard cells → ↓ $\\Psi_s$ → water in → turgid → open. Light promotes opening; CO₂ ↑ closes.',
        hindi: 'तंत्र: गार्ड कोशिकाओं द्वारा **K⁺ ग्रहण** → ↓ $\\Psi_s$ → पानी → गंदला → खुला। प्रकाश खुलने को बढ़ावा देता है; CO₂ ↑ बंद हो जाता है।',
            },
            {
              english: 'Factors affecting transpiration:',
        hindi: 'वाष्पोत्सर्जन को प्रभावित करने वाले कारक:',
            },
            {
              english: '• External: light, T, humidity, wind speed, water status.',
        hindi: '• बाहरी: प्रकाश, टी, आर्द्रता, हवा की गति, पानी की स्थिति।',
            },
            {
              english: '• Internal: leaf area, cuticle, stomatal number/distribution.',
        hindi: '• आंतरिक: पत्ती क्षेत्र, छल्ली, रंध्र number/distribution।',
            },
            {
              english: '**Transpiration : Water absorption ratio** very high — only ~ 1-5 % of absorbed water used in metabolism.',
        hindi: '**वाष्पोत्सर्जन: जल अवशोषण अनुपात** बहुत अधिक - चयापचय में अवशोषित पानी का केवल ~ 1-5% उपयोग किया जाता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'mineral-translocation',
      title: {
        english: 'Uptake of Mineral Ions & Phloem Translocation',
        hindi: 'खनिज आयनों का अवशोषण और फ्लोएम स्थानान्तरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Mineral absorption** — mostly active (against gradient) by root epidermal cells; uses ATP from root respiration; some ions enter passively.',
        hindi: '**खनिज अवशोषण** - जड़ एपिडर्मल कोशिकाओं द्वारा अधिकतर सक्रिय (ढाल के विपरीत); जड़ श्वसन से एटीपी का उपयोग करता है; कुछ आयन निष्क्रिय रूप से प्रवेश करते हैं।',
            },
            {
              english: 'Transport of minerals — as ions in **xylem stream** along with water; redistributed from older → younger leaves.',
        hindi: 'खनिजों का परिवहन - पानी के साथ **जाइलम धारा** में आयनों के रूप में; पुराने → युवा पत्तों से पुनर्वितरित।',
            },
            {
              english: '**Phloem transports food** — sucrose mainly; bidirectional.',
        hindi: '**फ्लोएम भोजन का परिवहन करता है** - मुख्य रूप से सुक्रोज; द्विदिशात्मक.',
            },
            {
              english: '**Münch\'s pressure-flow / mass-flow hypothesis**:',
        hindi: '**मुंच की दबाव-प्रवाह/द्रव्यमान-प्रवाह परिकल्पना**:',
            },
            {
              english: '• At **source** (leaf), sucrose loaded into sieve tubes → ↓ $\\Psi_w$ → water enters from xylem → builds **turgor pressure**.',
        hindi: '• **स्रोत** (पत्ती) पर, सुक्रोज को छलनी ट्यूबों में लोड किया जाता है → ↓ $\\Psi_w$ → पानी जाइलम से प्रवेश करता है → **टगर दबाव** बनाता है।',
            },
            {
              english: '• At **sink** (root, fruit), sugar unloaded → ↑ $\\Psi_w$ → water exits → pressure drops.',
        hindi: '• **सिंक** (जड़, फल) पर, चीनी उतारी गई → ↑ $\\Psi_w$ → पानी निकलता है → दबाव गिरता है।',
            },
            {
              english: '• Pressure difference → mass flow of phloem sap from source → sink.',
        hindi: '• दबाव अंतर → स्रोत से फ्लोएम रस का द्रव्यमान प्रवाह → सिंक।',
            },
            {
              english: 'Loading at source = active (companion cells via H⁺-sucrose symport, ATP-driven).',
        hindi: 'स्रोत पर लोड हो रहा है = सक्रिय (एच⁺-सुक्रोज सिम्पोर्ट के माध्यम से सहयोगी कोशिकाएं, एटीपी-संचालित)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\Psi_w = \\Psi_s + \\Psi_p',
      desc: {
        english: 'Water potential',
        hindi: 'जल क्षमता',
      },
    },
    {
      latex: '\\Psi_w(\\text{pure water}) = 0; \\text{ solute} \\Rightarrow \\Psi_w < 0',
      desc: {
        english: '',
        hindi: 'डब्ल्यू(शुद्ध पानी) = 0; विलेय w < 0',
      },
    },
    {
      latex: '\\text{Hypotonic} \\to \\text{turgid}; \\text{Hypertonic} \\to \\text{plasmolysed}',
      desc: {
        english: '',
        hindi: 'हाइपोटोनिक स्फीति; हाइपरटोनिक प्लास्मोलाइज्ड',
      },
    },
    {
      latex: '\\text{Casparian strip = suberin in endodermis}',
      desc: {
        english: '',
        hindi: 'कैस्पेरियन पट्टी = एण्डोडर्मिस में सुबेरिन',
      },
    },
    {
      latex: '\\text{Cohesion + Adhesion + Transpiration pull (Dixon)}',
      desc: {
        english: '',
        hindi: 'सामंजस्य + आसंजन + वाष्पोत्सर्जन खिंचाव (डिक्सन)',
      },
    },
    {
      latex: '\\text{Stomatal opening: K}^+ \\text{ uptake} \\to \\text{guard cells turgid}',
      desc: {
        english: '',
        hindi: 'रंध्र का खुलना: K + ग्रहण रक्षक कोशिकाएँ सुस्त',
      },
    },
    {
      latex: '\\text{Münch: source (high P)} \\to \\text{sink (low P) mass flow}',
      desc: {
        english: '',
        hindi: 'मंच: स्रोत (उच्च पी) सिंक (कम पी) द्रव्यमान प्रवाह',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Transport',
      problem: {
        english: 'Main driving force for ascent of sap in tall trees:',
        hindi: 'ऊंचे पेड़ों में रस के आरोहण के लिए मुख्य प्रेरक शक्ति:',
      },
      answer: {
        english: 'Transpiration pull (cohesion-tension)',
        hindi: 'वाष्पोत्सर्जन खिंचाव (सामंजस्य-तनाव)',
      },
      solution: {
        english: 'Root pressure too small for tall trees.',
        hindi: 'ऊंचे पेड़ों के लिए जड़ का दबाव बहुत छोटा है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Stomata',
      problem: {
        english: 'Opening of stomata is associated with:',
        hindi: 'रंध्रों का खुलना संबंधित है:',
      },
      answer: {
        english: 'Influx of K⁺ in guard cells',
        hindi: 'रक्षक कोशिकाओं में K⁺ का प्रवाह',
      },
      solution: {
        english: 'Lowers Ψw → water enters → turgid.',
        hindi: 'Ψw कम करता है → पानी प्रवेश करता है → गाढ़ा।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Pathway',
      problem: {
        english: 'Casparian strips are made of:',
        hindi: 'कैस्पेरियन पट्टियाँ निम्न से बनी होती हैं:',
      },
      answer: {
        english: 'Suberin',
        hindi: 'सुबेरिन',
      },
      solution: {
        english: 'Block apoplastic flow at endodermis.',
        hindi: 'एंडोडर्मिस पर एपोप्लास्टिक प्रवाह को अवरुद्ध करें।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Translocation',
      problem: {
        english: 'Phloem translocation is best explained by:',
        hindi: 'फ्लोएम ट्रांसलोकेशन को सबसे अच्छी तरह से समझाया गया है:',
      },
      answer: {
        english: 'Münch pressure-flow hypothesis',
        hindi: 'मंच दबाव-प्रवाह परिकल्पना',
      },
      solution: {
        english: 'Source-to-sink mass flow.',
        hindi: 'स्रोत-से-सिंक द्रव्यमान प्रवाह।',
      },
    },
    {
      year: 2019,
      exam: 'NEET',
      tag: 'Guttation',
      problem: {
        english: 'Guttation is caused by:',
        hindi: 'गुटेशन किसके कारण होता है:',
      },
      answer: {
        english: 'Root pressure',
        hindi: 'जड़ दबाव',
      },
      solution: {
        english: 'Through hydathodes early morning.',
        hindi: 'सुबह-सुबह हाइडैथोड के माध्यम से।',
      },
    },
  ],
  next: {
    slug: 'mineral-nutrition',
    title: 'Mineral Nutrition',
  },
};

export default chapter;
